import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// Delete folder dialog
//
// Default behavior is non-destructive: the folder and every document
// inside it get soft-deleted into Trash, recoverable for 30 days. The
// dialog is a simple confirm; the previous "force delete" (permanent
// purge) path stays accessible via the API but is intentionally not a
// one-click surface in the UI.
// ---------------------------------------------------------------------------

export interface DeleteFolderDialogProps {
  open: boolean;
  folderName: string;
  deletePending: boolean;
  deleteError: string | null;
  onCancel: () => void;
  onDelete: (opts?: { force?: boolean }) => void;
}

export function DeleteFolderDialog({
  open,
  folderName,
  deletePending,
  deleteError,
  onCancel,
  onDelete,
}: DeleteFolderDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move &ldquo;{folderName}&rdquo; to Trash?</DialogTitle>
          <DialogDescription>
            This folder and every document inside it (including subfolders) will be moved to Trash.
            You can restore documents from Trash for up to 30 days.
          </DialogDescription>
        </DialogHeader>

        {deleteError && (
          <p role="alert" className="rounded bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {deleteError}
          </p>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            disabled={deletePending}
            className="rounded border border-border bg-card px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onDelete()}
            disabled={deletePending}
            className="rounded bg-destructive px-3 py-1.5 text-sm text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
          >
            {deletePending ? 'Moving to Trash…' : 'Move to Trash'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
